// Where a Relatude.DB installation sends its admin to sign in. The request id in the url names a
// login request the installation registered over the back channel; the page shows which
// installation it is and where the browser would be sent back to, gets the user signed in if
// needed, then asks the server to authorize and sends the browser on with the one-time code.
// An installation or address the owner has not approved yet is approved here by the owner, in so
// many words, or refused for anyone else.
import { useEffect, useState } from 'react'
import { ApiError, approvalNeeded, connect, errorMessage, formatDate, type ApprovalNeeded, type LoginRequestInfo } from '../api'
import { useAuth } from '../auth'
import { AuthLayout } from '../components/AuthLayout'
import { SignInMethods } from '../components/SignInMethods'
import { Badge, ErrorText, Facts, Icon, Link, Notice, Spinner } from '../components/Ui'
import { currentUrl, useRoute } from '../router'

export function ConnectPage() {
  const route = useRoute()
  const requestId = route.query.get('request') ?? ''
  const { user, providers, setUser, signOut } = useAuth()
  const [info, setInfo] = useState<LoginRequestInfo | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [authorizing, setAuthorizing] = useState(false)
  const [denied, setDenied] = useState<string | null>(null)
  const [approval, setApproval] = useState<ApprovalNeeded | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!requestId) {
      setLoadError('This page needs a sign-in request from a Relatude.DB installation. Open the admin UI there and choose "Sign in with Relatude.License".')
      return
    }
    let cancelled = false
    connect
      .request(requestId)
      .then((r) => {
        if (!cancelled) setInfo(r)
      })
      .catch((err: unknown) => {
        if (!cancelled) setLoadError(errorMessage(err))
      })
    return () => {
      cancelled = true
    }
  }, [requestId])

  async function authorize(approve = false) {
    setAuthorizing(true)
    setError(null)
    setDenied(null)
    setApproval(null)
    try {
      const result = await connect.authorize(requestId, approve)
      window.location.assign(result.redirectTo)
    } catch (err) {
      const needed = approvalNeeded(err)
      if (needed) setApproval(needed)
      else if (err instanceof ApiError && err.status === 403) setDenied(err.message)
      else setError(errorMessage(err))
      setAuthorizing(false)
    }
  }

  return (
    <AuthLayout
      title="Signing in to a Relatude.DB installation."
      lead="An installation has sent you here to sign in with your Relatude.License account. Check that it is the installation you expect, and that it will send you back to the right address, before you continue."
      points={[
        {
          icon: 'shield',
          title: 'Only approved installations',
          text: 'The owner of the license approves each installation and the addresses its admin UI is opened on. A copy of the license keys alone is not enough to get someone signed in.',
        },
        {
          icon: 'users',
          title: 'Who may sign in',
          text: 'The owner can sign in to everything under the license. Everyone else needs a grant from the owner, given by email address or mobile number.',
        },
        {
          icon: 'key',
          title: 'One account, every installation',
          text: 'The same account and the same passkey work at every installation you have access to; nothing to set up on each one.',
        },
      ]}
    >
      {loadError ? (
        <>
          <h1 className="panel-title">Sign-in request</h1>
          <p className="panel-lead">The request this page was opened with could not be used.</p>
          <ErrorText error={loadError} />
          <p className="signin-footnote">
            You can still <Link to="/">go to your licenses</Link>, and start the sign-in again from the installation's admin UI.
          </p>
        </>
      ) : !info ? (
        <Spinner label="Looking up the sign-in request…" />
      ) : (
        <>
          <h1 className="panel-title">Sign in to {info.installationName}</h1>
          <p className="panel-lead">
            {user
              ? 'Check the details, then continue. You will be sent back to the installation, signed in.'
              : 'Sign in with your Relatude.License account first. Then you will be sent back to the installation, signed in.'}
          </p>
          <div className="request">
            <div className="request-name">
              <Icon name="server" />
              <span>{info.installationName}</span>
              {!info.hostApproved && <Badge tone="warn">{info.installationApproved ? 'Address not yet approved' : 'Not yet approved for sign-in'}</Badge>}
            </div>
            <Facts
              items={[
                { label: 'License', value: info.licenseName },
                { label: 'Back to', value: <code>{info.redirectHost}</code> },
                { label: 'Request valid until', value: formatDate(info.expiresUtc) },
              ]}
            />
          </div>
          {!user ? (
            <SignInMethods providers={providers} returnUrl={currentUrl()} onSignedIn={(result) => setUser(result.user)} />
          ) : (
            <div className="connect-actions">
              <p>
                Signed in as <strong>{user.displayName}</strong>
                {user.email && user.displayName !== user.email ? ` (${user.email})` : ''}.
              </p>
              {approval && (
                <div className="approval">
                  <Notice kind="warn">
                    <p>
                      <strong>
                        {approval.installationApproved
                          ? `${approval.host} is a new address for ${approval.installationName}.`
                          : `${approval.installationName} has not been approved for sign-in yet.`}
                      </strong>
                    </p>
                    <p>
                      As the owner of the license you can approve it now. Do so only if <code>{approval.host}</code> is where your installation really
                      lives: anyone holding your license keys could send you to this page from an address of their own, and the address is the one thing
                      they cannot fake.
                    </p>
                  </Notice>
                  <div className="row">
                    <button type="button" className="button button-primary" disabled={authorizing} onClick={() => authorize(true)}>
                      {authorizing ? 'Continuing…' : `Approve ${approval.host} and continue`}
                    </button>
                    <button type="button" className="button button-quiet" disabled={authorizing} onClick={() => setApproval(null)}>
                      Not now
                    </button>
                  </div>
                </div>
              )}
              {denied && (
                <Notice kind="warn">
                  {denied} You are signed in as {user.displayName}; if you have another account,{' '}
                  <a
                    href="#"
                    onClick={async (e) => {
                      e.preventDefault()
                      await signOut()
                      setDenied(null)
                    }}
                  >
                    switch account
                  </a>
                  .
                </Notice>
              )}
              <ErrorText error={error} />
              {!approval && (
                <div className="row">
                  <button type="button" className="button button-primary" disabled={authorizing} onClick={() => authorize()}>
                    {authorizing ? 'Continuing…' : `Continue to ${info.installationName}`}
                  </button>
                  <button type="button" className="button button-quiet" disabled={authorizing} onClick={() => void signOut()}>
                    Not you? Sign out
                  </button>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </AuthLayout>
  )
}
